import { ChevronDown, ChevronUp } from "lucide-react";
import { useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";

import {
  describeTacticDraftFix,
  formatTacticDraftValue,
  tacticDraftAllowsExperiment,
  tacticDraftConflictMessage,
  type TacticDraft,
} from "../data/cockpit-data";
import { postTacticDraftAction, type TacticDraftAction } from "../data/queries";
import { ItemTraceabilityByKey } from "./ItemTraceabilityTimeline";
import { Notice } from "./Notice";

const ACTION_LABELS: Record<TacticDraftAction, string> = {
  apply: "Apply",
  change: "Change",
  remove: "Remove",
};

const ACTION_BUSY_LABELS: Record<TacticDraftAction, string> = {
  apply: "Applying…",
  change: "Changing…",
  remove: "Removing…",
};

/**
 * One ranked draft, and the three things an operator can do about it.
 *
 * The 409 branch is the interesting one: a draft can be SUPERSEDED between the
 * page loading and the click, and the reply says so rather than failing
 * generically. Treating that as an error would send someone hunting for a bug
 * in a system that behaved correctly — the draft simply moved on.
 */
export function TacticDraftCard({
  draft,
  onMutated,
  onConflict,
}: {
  draft: TacticDraft;
  onMutated: () => void;
  onConflict: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [runAsExperiment, setRunAsExperiment] = useState(false);
  const [busyAction, setBusyAction] = useState<TacticDraftAction | null>(null);
  const [status, setStatus] = useState(draft.status);
  const [error, setError] = useState<string | null>(null);
  const [superseded, setSuperseded] = useState<string | null>(null);
  const fix = describeTacticDraftFix(draft);
  const canExperiment = tacticDraftAllowsExperiment(draft);

  async function act(action: TacticDraftAction) {
    setBusyAction(action);
    setStatus(`${action} pending`);
    setError(null);
    setSuperseded(null);
    try {
      const result = await postTacticDraftAction(
        action,
        draft.draft_id,
        canExperiment ? runAsExperiment : undefined,
      );
      if (result.status === 409) {
        setStatus(draft.status);
        setSuperseded(tacticDraftConflictMessage(result.body));
        onConflict();
        return;
      }
      if (result.status === 403) {
        setStatus(draft.status);
        setError("You are not permitted to change tactic drafts.");
        return;
      }
      if (!result.ok) {
        setStatus(draft.status);
        setError(
          typeof result.body.detail === "string"
            ? result.body.detail
            : typeof result.body.error === "string"
              ? result.body.error
              : "The tactic action failed.",
        );
        return;
      }
      setStatus(action === "apply" ? "applied" : action === "change" ? "changed" : "removed");
      onMutated();
    } catch {
      setStatus(draft.status);
      setError("The tactic action failed. Check the connection and retry.");
    } finally {
      setBusyAction(null);
    }
  }

  return (
    <Card>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 text-xs font-medium text-muted-foreground">
              {draft.band_rank !== null && <span>Rank {draft.band_rank}</span>}
              <span>{draft.signal_id}</span>
              <span>{draft.confidence_tier}</span>
              <span>{draft.value_basis}</span>
              {draft.tactic_version !== null && <span>tactic v{draft.tactic_version}</span>}
            </div>
            <h3 className="mt-2 text-[15px] font-semibold text-ks-ink">
              {draft.keyword} <span className="font-normal text-muted-foreground">×</span> {draft.market}
              {draft.asin && (
                <>
                  <span className="font-normal text-muted-foreground"> × </span>
                  {draft.asin}
                </>
              )}
            </h3>
          </div>
          {status !== draft.status && (
            <Badge variant="default" role="status">
              {status}
            </Badge>
          )}
        </div>

        <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Fix</p>
            <p className="mt-1 text-sm font-semibold text-ks-ink">{fix.title}</p>
          </div>
          <div className="sm:text-right">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Expected value
            </p>
            <p
              className={`mt-1 text-sm font-semibold ${
                draft.expected_delta_value === null ? "text-muted-foreground" : "text-ks-ink"
              }`}
            >
              {formatTacticDraftValue(draft.expected_delta_value)}
            </p>
          </div>
        </div>

        {superseded && (
          <Notice title={superseded}>
            <Button variant="link" size="sm" onClick={onConflict}>
              Re-fetch current draft
            </Button>
          </Notice>
        )}
        {error && (
          <p className="text-sm font-medium text-ks-danger" role="alert">
            {error}
          </p>
        )}

        <div className="flex flex-wrap items-center gap-2 border-t border-ks-grey-3 pt-4">
          {(["apply", "change", "remove"] as const).map((action) => (
            <Button
              key={action}
              size="lg"
              variant={action === "apply" ? "default" : "outline"}
              disabled={busyAction !== null}
              onClick={() => void act(action)}
            >
              {busyAction === action ? ACTION_BUSY_LABELS[action] : ACTION_LABELS[action]}
            </Button>
          ))}
          {canExperiment && (
            <label className="ml-auto inline-flex items-center gap-2 text-xs font-medium text-muted-foreground">
              <input
                type="checkbox"
                checked={runAsExperiment}
                disabled={busyAction !== null}
                onChange={(event) => setRunAsExperiment(event.target.checked)}
              />
              Run as experiment
            </label>
          )}
        </div>

        <Button
          variant="ghost"
          size="xs"
          className="self-start"
          aria-expanded={expanded}
          onClick={() => setExpanded((value) => !value)}
        >
          {expanded ? <ChevronUp aria-hidden="true" /> : <ChevronDown aria-hidden="true" />}
          {expanded ? "Hide detail" : "Open detail"}
        </Button>

        {expanded && (
          <div className="flex flex-col gap-4 rounded-lg border border-ks-grey-3 bg-muted/40 p-4">
            <dl className="grid gap-3 text-xs sm:grid-cols-3">
              <Detail label="Draft" value={draft.draft_id} />
              <Detail label="Tactic" value={draft.tactic_id} />
              <Detail label="Item key" value={draft.item_key} />
              <Detail label="Delta" value={draft.delta_kind} />
              <Detail label="Action" value={draft.action_type} />
              <Detail label="Value model" value={draft.value_model} />
            </dl>
            {fix.params.length > 0 && (
              <dl className="grid gap-2 border-t border-ks-grey-3 pt-3 text-xs sm:grid-cols-2">
                {fix.params.map(([label, value]) => (
                  <Detail key={label} label={label} value={value} />
                ))}
              </dl>
            )}
            {/* Disabled, and SAID to be — an enabled-looking box that silently
                discards what someone typed is worse than no box at all. */}
            <label className="block text-xs font-semibold text-muted-foreground">
              Operator notes
              <Textarea
                disabled
                className="mt-2 min-h-16"
                placeholder="Notes coming with backend tactic note route"
              />
              <span className="mt-1 block font-normal text-muted-foreground">
                Notes coming with the backend tactic note route.
              </span>
            </label>
            <div className="border-t border-ks-grey-3 pt-4">
              <ItemTraceabilityByKey itemKey={draft.item_key} />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="font-semibold text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 break-words text-ks-ink">{value}</dd>
    </div>
  );
}
